/**
 * Accessibility fixes — Eleanor + Hobbs (added Oct 2026)
 *
 * Small, self-contained enhancements layered on top of Symmetry's own scripts.
 * Nothing here changes how the store looks; it only adds information for
 * keyboard and screen-reader users. Safe to remove as a unit if ever needed
 * (also delete the matching line in layout/theme.liquid).
 *
 *  1. Links used as buttons (href="#") are announced as buttons and respond to
 *     the Space key, like real buttons do.
 *  2. Mobile menu sub-menu toggles report whether they are open (aria-expanded).
 *  3. Pause / play control for the scrolling logo banner (WCAG 2.2.2).
 *  4. Product card, blog and cart photos open their page when clicked. Each photo
 *     used to be a second link to the same page as the title next to it; now the
 *     title is the one real link and the photo just forwards clicks.
 */
(() => {
  /* 1. Links that act as buttons ------------------------------------------ */
  const PSEUDO_BUTTON = 'a[href="#"]:not([role]):not([aria-hidden="true"]):not(.navigation__link)';

  const markPseudoButtons = (root = document) => {
    root.querySelectorAll(PSEUDO_BUTTON).forEach((el) => el.setAttribute('role', 'button'));
  };

  document.addEventListener('keydown', (evt) => {
    if (evt.key !== ' ' && evt.key !== 'Spacebar') return;
    const el = evt.target;
    if (el && el.matches && el.matches('a[role="button"]')) {
      evt.preventDefault();
      el.click();
    }
  });

  // Content is swapped in by AJAX (filters, quick buy, cart drawer), so re-check after changes.
  let pending = false;
  const observer = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      markPseudoButtons();
      syncMenuToggles();
    });
  });

  /* 2. Sub-menu toggles: expose open / closed state ------------------------- */
  const syncMenuToggles = () => {
    document.querySelectorAll('.navigation__item--with-children > .navigation__children-toggle').forEach((toggle) => {
      const isOpen = toggle.parentElement.classList.contains('navigation__item--open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  };

  document.addEventListener('click', (evt) => {
    if (evt.target.closest && evt.target.closest('.navigation__children-toggle, .mobile-nav-back, .mobile-nav-toggle')) {
      setTimeout(syncMenuToggles, 0);
    }
  });

  /* 3. Scrolling banner pause / play ---------------------------------------- */
  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest && evt.target.closest('.a11y-marquee-toggle');
    if (!btn) return;
    const container = btn.closest('.marquee-container');
    const paused = btn.getAttribute('aria-pressed') !== 'true';
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (container) container.classList.toggle('a11y-marquee--paused', paused);
  });

  /* 4. Photos that forward clicks to the link beside them ------------------ */
  const clickThroughUrl = (target) => {
    if (!target || !target.closest) return null;
    const media = target.closest('.product-block__media-link');
    if (media) {
      const block = media.closest('product-block');
      const link = block && block.querySelector('.product-block__detail a.product-link');
      return link ? link.href : null;
    }
    const el = target.closest('[data-a11y-href]');
    return el ? el.getAttribute('data-a11y-href') : null;
  };

  document.addEventListener('click', (evt) => {
    if (evt.defaultPrevented || evt.button !== 0) return;
    const url = clickThroughUrl(evt.target);
    if (!url) return;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  });

  // Middle-click opens a new tab, as it would on a normal link
  document.addEventListener('auxclick', (evt) => {
    if (evt.button !== 1) return;
    const url = clickThroughUrl(evt.target);
    if (url) window.open(url, '_blank');
  });

  const init = () => {
    markPseudoButtons();
    syncMenuToggles();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
